import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { KeycloakService } from 'keycloak-angular';
import { UserProfile, ThemePreference } from '../models/user.model';
import { ThemeService } from './theme.service';
import { UserStorageService } from './user-storage.service';
import { firstValueFrom, timeout } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private keycloak = inject(KeycloakService);
  private http     = inject(HttpClient);
  private themes   = inject(ThemeService);
  private userStorage = inject(UserStorageService);

  readonly currentUser = signal<UserProfile | null>(null);

  /**
   * Perché il backend ha rifiutato l'utente, se lo ha rifiutato: il codice
   * dell'intestazione `X-Accesso-Negato` (`nessun_ruolo`, `email_mancante`,
   * `limite_licenza`, …). Quando è valorizzato le guardie portano sulla pagina
   * `/accesso-negato`, che lo spiega.
   */
  readonly accessoNegato = signal<string | null>(null);

  async init(): Promise<void> {
    if (!(await this.keycloak.isLoggedIn())) return;

    // Il profilo (ruolo + permessi) DEVE essere caricato prima che i guard
    // valutino le route: se currentUser resta null, has()/hasRole ritornano
    // false e l'utente — anche super-user — viene sbattuto su /unauthorized.
    //
    // Due insidie viste in produzione:
    //  1) al primo ritorno dal login (code PKCE appena scambiato) il token può
    //     non essere ancora propagato → la GET /me parte senza Authorization e
    //     prende 401; updateToken() prima della chiamata garantisce il token.
    //  2) un errore transitorio del backend (es. DB non ancora pronto) lasciava
    //     currentUser=null SENZA recupero → permessi a zero per tutta la sessione.
    // Quindi: assicuriamo il token, poi ritentiamo /me con backoff breve.
    try {
      await this.keycloak.updateToken(30);
    } catch {
      /* il token corrente potrebbe bastare comunque: proseguiamo */
    }

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const profile = await firstValueFrom(
          this.http.get<UserProfile>('/api/v1/auth/me')
        );
        this.currentUser.set(profile);
        // Before anything else: from here on the state saved in the browser
        // (filters, personal theme, caches) is this user's.
        this.userStorage.setUser(profile.id);
        this.themes.applyUserTheme();
        this.applyMode(profile.theme_preference ?? 'system');
        return;
      } catch (err) {
        // Un rifiuto motivato non è un guasto transitorio: ritentare non serve,
        // e il logout di prima riportava l'utente alla richiesta di credenziali
        // senza dirgli niente — con il portale che lo riconosceva subito, era
        // un giro senza uscita.
        const codice = err instanceof HttpErrorResponse && err.status === 403
          ? err.headers.get('X-Accesso-Negato')
          : null;
        if (codice) {
          this.accessoNegato.set(codice);
          return;
        }
        console.error(`auth.init: GET /me fallita (tentativo ${attempt}/3)`, err);
        if (attempt < 3) {
          await new Promise(r => setTimeout(r, 400 * attempt));
        }
      }
    }
    // Profilo non caricabile: meglio rimandare al login (sessione coerente) che
    // restare loggati in Keycloak ma senza profilo → guard che negano tutto.
    console.error('auth.init: profilo non caricabile dopo 3 tentativi, eseguo logout');
    await this.logout();
  }

  applyMode(preference: ThemePreference): void {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const useDark = preference === 'dark' || (preference === 'system' && prefersDark);
    document.documentElement.setAttribute('data-theme', useDark ? 'dark' : 'light');
  }

  // kept for backwards compat (shell calls this)
  applyTheme(preference: ThemePreference): void {
    this.applyMode(preference);
  }

  listenSystemTheme(): void {
    window.matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', e => {
        if (this.currentUser()?.theme_preference === 'system') {
          document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
        }
      });
  }

  async saveThemePreference(preference: ThemePreference): Promise<void> {
    await firstValueFrom(
      this.http.patch('/api/v1/auth/users/me/preferences', { theme_preference: preference })
    );
    this.currentUser.update(u => u ? { ...u, theme_preference: preference } : u);
    this.applyMode(preference);
  }

  hasRole(role: string): boolean {
    return this.keycloak.isUserInRole(role);
  }

  isAdmin(): boolean {
    return this.hasRole('admin') || this.hasRole('super-user');
  }

  getToken(): string {
    // `?.`: prima di keycloak.init() l'istanza non esiste. Le chiamate
    // pre-login (branding, tema globale) passano dall'interceptor come tutte:
    // senza questo optional chaining morivano qui con un TypeError silenzioso,
    // e i loro catchError ripiegavano sul default — il title restava
    // «Saragraph» qualunque cosa dicesse la configurazione.
    return this.keycloak.getKeycloakInstance()?.token ?? '';
  }

  /**
   * Esce e torna sulla radice, dove nginx sceglie la lingua come per qualunque
   * altro ingresso — cookie della lingua scelta, altrimenti `Accept-Language`.
   *
   * Prima puntava a `/login`, che in una build per lingua non esiste: nginx lo
   * ripescava con la `location @lingua` e ci si ritrovava su `/it/login`, con
   * un reindirizzamento in più e la lingua decisa dal cookie invece che dalla
   * sessione appena chiusa.
   *
   * Senza barra finale di proposito: `window.location.origin` è esattamente la
   * forma elencata fra i *Valid post logout redirect URIs* del client Keycloak
   * (vedi backend/KEYCLOAK.md), e questo indirizzo lo valida Keycloak, non noi.
   */
  async logout(): Promise<void> {
    // L'uscita resta nel registro delle operazioni: il backend la registra
    // prima che il portale chiuda la sessione. È un di più, non una condizione:
    // se il backend non risponde entro due secondi si esce lo stesso.
    if (this.currentUser()) {
      try {
        await firstValueFrom(this.http.post('/api/v1/auth/logout', {}).pipe(timeout(2000)));
      } catch {
        /* si esce comunque */
      }
    }
    await this.keycloak.logout(window.location.origin);
  }

  /** Il nome utente del portale, anche quando il profilo Saragraph manca. */
  nomeUtentePortale(): string {
    const t = this.keycloak.getKeycloakInstance()?.tokenParsed as { preferred_username?: string } | undefined;
    return t?.preferred_username ?? '';
  }
}
